<template>
    <div id="auto-response" class="container-fluid main-content page-content">
        <div class="page-header">
            <div>
                <h3 class="page-title">{{ $t('auto-response') }}</h3>
            </div>
        </div>
        <popover name="create-auto-response" class="auto-response" event="hover" :width="335" id="create-auto-response">
            <p class="mb-0 fs-14 text-left p-2">
                {{ $t('cocoapp-auto-response') }}
            </p>
        </popover>
        <popover name="auto-response-keywords" class="auto-response" event="hover" :width="335" id="auto-response-keywords">
            <p class="mb-0 fs-14 text-left p-2" v-html="$t('auto-response-keywords')"></p>
        </popover>
        <div class="page-card">
            <div class="d-flex justify-content-between px-3 py-1">
                <div class="col-6">
                    <div class="position-relative">
                        <input
                            v-if="!firstLoading"
                            type="text"
                            class="form-control text-small py-1 pr-4"
                            v-bind:placeholder="$t('search-in-title-and-keywords')"
                            v-model.trim="search"
                            @keyup="getAutoResponses()"
                        >

                        <span
                            v-if="search"
                            class="clear-search"
                            @click="() => {
                                search = ''
                                getAutoResponses()
                            }"
                        >
                            &times;
                        </span>
                    </div>
                </div>
                <div class="col-6 d-flex justify-content-end">
                    <button class="btn btn-primary rounded-pill d-flex px-5 py-2" @click="createAutoResponse()">
                        {{ $t('add-auto-response') }}
                    </button>
                </div>
            </div>
            <base-table
                fill
                v-if="!firstLoading"
                :columns="columns"
                :items="autoResponses"
                :loading="loading"
                :empty-text="$t('no-results-found')"
            >
                <template #cell(keywords)="{ item }">
                    <div class="form-group m-0 d-flex flex-wrap" v-if="item.attributes.keywords.length > 0">
                        <p v-for="keyword in item.attributes.keywords" :key="keyword">
                            {{ keyword }}
                        </p>
                    </div>
                    <span v-else class="m-0 fs-12 text-dark-brown font-nunito-bold">
                        {{ $t('without-keyword') }}
                    </span>
                </template>
                <template #cell(actions)="{ item, index }">
                    <div class="d-flex align-items-center justify-content-end">
                        <div class="custom-control custom-switch">
                            <input
                                type="checkbox"
                                class="custom-control-input"
                                :checked="item.attributes.isEnabled"
                                @click="enabledOrDisabled(index)"
                                :id="`customSwitch-${index}`"
                            >
                            <label class="custom-control-label" :for="`customSwitch-${index}`"></label>
                        </div>
                        <button
                            type="button"
                            class="base-table-action"
                            :aria-label="$t('edit')"
                            @click="editAutoResponse(item.id, item.attributes.name)"
                        >
                            <font-awesome-icon :icon="['fas', 'pencil-alt']" />
                        </button>
                        <button
                            type="button"
                            class="base-table-action base-table-action-danger"
                            :aria-label="$t('delete')"
                            @click="removeAutoResponse(item.id, item.attributes.name)"
                        >
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </div>
                </template>
            </base-table>
            <div v-if="firstLoading" class="info pt-5 text-left">
                <p class="mt-5 fs-14 text-dark-brown">
                    {{ $t('autoresponse-description') }}
                </p>
                <p class="mt-2 fs-14 text-dark-brown">
                    {{ $t('received-sms-keyword') }}
                </p>
                <img class="w-100 text-center p-3" src="@/core/assets/images/auto-response.png" alt="">
            </div>
        </div>
        <auto-response-modal
            class="auto-response-sidebar"
            :editMode="editMode"
        >
        </auto-response-modal>
    </div>
</template>
<script>

// Internal repository
import Alert from '../../helpers/Alert.js';

// Components
import AutoResponseModal    from '@/core/components/AutoResponseModal.vue';
import BaseTable            from '@/core/components/BaseTable.vue';

// Import external libraries
import { mapActions, mapState } from 'vuex';

export default {
    components: {
        AutoResponseModal,
        BaseTable
    },

    data () {
        return {
            editMode:           false,
            search:             '',
            disabled:           true,
            loading:            false,
            firstLoading:       false,
            existAutoResponses: false
        };
    },

    created () {
        this.loading = true;

        this.index({ filters: null })
            .then(() => {
                this.loading = false;
            });
    },

    watch: {
        autoResponses () {
            this.existAutoResponses = this.autoResponses.length !== 0;

            if (this.existAutoResponses) {
                this.firstLoading = false;
            } else {
                this.firstLoading = true;
            }
        }
    },

    computed: {
        ...mapState('core/autoResponses', { autoResponses: 'autoResponses' }),

        columns () {
            return [
                {
                    key:     'attributes.name',
                    label:   this.$t('auto-response-title'),
                    tdClass: 'text-dark-brown'
                },
                {
                    key:     'keywords',
                    label:   this.$t('keywords'),
                    tdClass: 'cell-chips'
                },
                {
                    key:     'attributes.content',
                    label:   this.$t('autoresponse-sent'),
                    tdClass: 'cell-ellipsis'
                },
                {
                    key:     'actions',
                    label:   '',
                    thStyle: { width: '180px' },
                    tdClass: 'cell-actions'
                }
            ];
        }
    },

    methods: {
        ...mapActions('core/autoResponses', ['index', 'update', 'delete', 'selectAutoResponse']),

        /**
         * Show modal to create new auto-response
         */
        createAutoResponse () {
            this.editMode = false;
            this.$bvModal.show('auto-response-sidebar');
        },

        /**
         * Load the auto responses
         */
        getAutoResponses () {
            this.loading = true;
            let filters  = null;

            if (this.search !== '') {
                filters = { 'search': this.search };
            }

            const searchDelay = 1000;
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                this.index({ filters })
                    .then(() => {
                        this.loading      = false;
                        this.firstLoading = false;
                    });
            }, searchDelay);
        },

        /**
         * Enable or disable an auto response
         *
         * @param {integer}  index  the auto response index
         * @return void
         */
        enabledOrDisabled (index) {
            this.update({
                id:         this.autoResponses[index].id,
                attributes: { 'is-enabled': !this.autoResponses[index].attributes.isEnabled }
            })
                .catch((errors) => {
                    console.log(errors);
                });
        },

        /**
         * Detele auto response
         *
         * @param   {integer}   id          The id of the auto response.
         * @param   {string}    name        The name of the auto response.
         *
         * @return  void
         */
        removeAutoResponse (id, name) {
            this.$dialog.confirm({
                subject:     name,
                text:        this.$t('confirm-auto-response-delete'),
                centerText:  true,
                confirmText: this.$t('delete'),
                danger:      true,
                onConfirm:   () => this.delete({ id })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        Alert.success(this.$i18n.t('auto-response-deleted-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                    this.getAutoResponses();
                });
        },

        /**
         * Update auto response
         *
         * @param {integer} id The id of the auto response.
         *
         * @return void
         */
        editAutoResponse (id) {
            this.editMode = true;
            this.selectAutoResponse({ id })
                .then(() => {
                    this.$bvModal.show('auto-response-sidebar');
                });
        }
    }
};
</script>
